import { useCallback, useEffect, useRef, useState } from 'react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Breadcrumbs,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Link as MuiLink,
  Skeleton,
  TablePagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { Home as HomeIcon, NavigateNext as NavigateNextIcon } from '@mui/icons-material'
import { Link, Link as RouterLink } from 'react-router-dom'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { ttsApi } from '@/api/tts'
import { formatMoney } from '@/components/billing/formatMoney'
import { getErrorMessage } from '@/utils/error'
import { formatDateTimeDMY } from '@/utils/date'
import { ROUTES } from '@/utils/constants'
import type { TtsBatch } from '@/types/tts'
import '@/styles/dashboard.css'

const REFRESH_INTERVAL_MS = 5000

function wordsSummary(batch: TtsBatch): string {
  const texts = (batch.items ?? [])
    .map((item) => item.word_text)
    .filter((text): text is string => Boolean(text))
  if (texts.length === 0) return '—'
  const shown = texts.slice(0, 3).join(', ')
  return texts.length > 3 ? `${shown} +${texts.length - 3}` : shown
}

export function TtsTasksPage() {
  const { t } = useTranslation()
  const [batches, setBatches] = useState<TtsBatch[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(15)
  const [total, setTotal] = useState(0)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const load = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true)
      try {
        const result = await ttsApi.listBatches(page, perPage)
        setBatches(result.data)
        setTotal(result.meta?.total ?? result.data.length)
        setError(null)
      } catch (err) {
        setError(getErrorMessage(err) || t('common.error'))
      } finally {
        if (!silent) setLoading(false)
      }
    },
    [page, perPage, t],
  )

  useEffect(() => {
    void load()
  }, [load])

  // Автообновление, пока есть задачи в обработке
  const hasProcessing = batches.some((b) => b.status === 'processing')
  useEffect(() => {
    if (!hasProcessing) return
    timerRef.current = setTimeout(() => void load(true), REFRESH_INTERVAL_MS)
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [hasProcessing, batches, load])

  return (
    <DashboardLayout>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link
            to={ROUTES.DASHBOARD}
            style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            {t('dashboard.home')}
          </Link>
          <MuiLink component={RouterLink} to={ROUTES.WORDS} underline="hover" color="inherit">
            {t('words.title', 'Слова')}
          </MuiLink>
          <Typography color="text.primary">{t('tts.tasksTitle', 'Задачи озвучки')}</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Typography variant="h4" component="h1" fontWeight={700}>
            {t('tts.tasksTitle', 'Задачи озвучки')}
          </Typography>
          <PageHelpPopover pageKey="tts-tasks" />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Card>
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            {loading ? (
              <Box sx={{ p: 2 }}>
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} variant="rectangular" height={52} sx={{ mb: 1, borderRadius: 1 }} />
                ))}
              </Box>
            ) : batches.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">
                  {t('tts.tasksEmpty', 'Задач озвучки пока нет')}
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('tts.tasksCreatedAt', 'Дата')}</TableCell>
                      <TableCell>{t('tts.tasksWords', 'Слова')}</TableCell>
                      <TableCell align="right">{t('tts.tasksCount', 'Слов')}</TableCell>
                      <TableCell align="right">{t('tts.price', 'Цена')}</TableCell>
                      <TableCell sx={{ minWidth: 180 }}>{t('tts.tasksStatus', 'Статус')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {batches.map((batch) => {
                      const processed = batch.done_count + batch.failed_count + batch.skipped_count
                      const progress = batch.total_count > 0 ? (processed / batch.total_count) * 100 : 0
                      const failedErrors = (batch.items ?? [])
                        .filter((item) => item.status === 'failed' && item.error)
                        .map((item) => `${item.word_text ?? item.word_id}: ${item.error}`)
                        .join('\n')

                      return (
                        <TableRow key={batch.id} hover>
                          <TableCell>
                            {formatDateTimeDMY(batch.created_at)}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ maxWidth: 320 }} noWrap>
                              {wordsSummary(batch)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{batch.total_count}</TableCell>
                          <TableCell align="right">{formatMoney(batch.total_price)}</TableCell>
                          <TableCell>
                            {batch.status === 'processing' ? (
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  {t('tts.tasksProcessing', 'Обрабатывается')} ({processed}/{batch.total_count})
                                </Typography>
                                <LinearProgress variant="determinate" value={progress} sx={{ mt: 0.5 }} />
                              </Box>
                            ) : (
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                <Chip
                                  size="small"
                                  color="success"
                                  label={`${t('tts.done', 'озвучено')}: ${batch.done_count}`}
                                />
                                {batch.skipped_count > 0 && (
                                  <Chip
                                    size="small"
                                    label={`${t('tts.skipped', 'пропущено')}: ${batch.skipped_count}`}
                                  />
                                )}
                                {batch.failed_count > 0 && (
                                  <Tooltip title={<Box sx={{ whiteSpace: 'pre-line' }}>{failedErrors}</Box>}>
                                    <Chip
                                      size="small"
                                      color="error"
                                      label={`${t('tts.failed', 'ошибок')}: ${batch.failed_count}`}
                                    />
                                  </Tooltip>
                                )}
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {!loading && total > 0 && (
          <TablePagination
            component="div"
            count={total}
            page={Math.max(0, page - 1)}
            onPageChange={(_, p) => setPage(p + 1)}
            rowsPerPage={perPage}
            onRowsPerPageChange={(e) => {
              setPerPage(parseInt(e.target.value, 10))
              setPage(1)
            }}
            rowsPerPageOptions={[10, 15, 25, 50]}
            labelRowsPerPage={t('words.rows_per_page')}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}–${to} ${t('words.of')} ${count !== -1 ? count : `${t('words.more_than')} ${to}`}`
            }
          />
        )}
      </Box>
    </DashboardLayout>
  )
}
